import "./style.css";
import { DEFAULT_SETTINGS, WORKS } from "./data.js";
import { mountControl, mountCurtain, mountHeart, mountPuppet } from "./renderers/canvas-scenes.js";
import { mountRain, mountTyping, mountYanfan } from "./renderers/dom-scenes.js";
import { mountSparks } from "./renderers/webgl-scene.js";

const app = document.querySelector("#app");
const settings = { ...DEFAULT_SETTINGS };
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
let activeCleanup = null;
let activeWork = null;

const mounts = {
  heart: mountHeart,
  yanfan: mountYanfan,
  puppet: mountPuppet,
  typing: mountTyping,
  control: mountControl,
  sparks: mountSparks,
  rain: mountRain,
  curtain: mountCurtain,
};

function esc(value) {
  return value.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" })[char]);
}

function galleryMarkup() {
  return `
    <div class="gallery-shell">
      <header class="gallery-header">
        <div class="header-index" aria-hidden="true">01—08</div>
        <div class="mark"></div>
        <p class="eyebrow">PROGRAMMATIC MOTION / 2026</p>
        <h1><span>PLAY</span> DIFFERENT<br><em>多渲染器交互动画集</em></h1>
        <div class="intro-grid">
          <p class="lede">同一类诗意交互，不再绑定 p5.js。八个场景分别落在 Canvas 2D、SVG、DOM、WAAPI、原生 WebGL 与 CSS 滤镜上。</p>
          <p class="note">每件作品都能直接操作。按 <kbd>G</kbd> 打开调试台，观察画质、粒子密度、时间倍率与降级路径。</p>
        </div>
      </header>
      <main class="works" aria-label="交互动画作品">
        ${WORKS.map(
          (work, index) => `
            <article class="work work--${esc(work.tone)}" style="--i:${index}">
              <button class="preview" type="button" data-scene="${esc(work.id)}" aria-label="进入 ${esc(work.title)} ${esc(work.subtitle)}">
                <canvas data-preview="${esc(work.id)}" aria-hidden="true"></canvas>
                <span class="preview-index">${String(index + 1).padStart(2, "0")}</span>
                <span class="preview-action">ENTER ↗</span>
              </button>
              <div class="work-copy">
                <h2>${esc(work.title)}<small>${esc(work.subtitle)}</small></h2>
                <p>${esc(work.desc)}</p>
                <div class="work-meta"><span>${esc(work.renderer)}</span><span>LIVE</span></div>
              </div>
            </article>`,
        ).join("")}
      </main>
      <footer class="gallery-footer">
        <span>NO P5.JS · ZERO RUNTIME DEPENDENCY</span>
        <span>CANVAS / SVG / DOM / WAAPI / WEBGL / CSS</span>
        <button type="button" data-open-debug>DEBUG GUI — G</button>
      </footer>
    </div>`;
}

function debugMarkup() {
  return `
    <aside class="debug-panel" aria-label="渲染调试面板" aria-hidden="true">
      <div class="debug-head">
        <span>RENDER LAB</span>
        <button type="button" data-close-debug aria-label="关闭调试面板">×</button>
      </div>
      <label>QUALITY
        <select data-setting="quality">
          <option value="auto">AUTO</option>
          <option value="high">HIGH</option>
          <option value="eco">ECO / FALLBACK</option>
        </select>
      </label>
      <label>SPEED <output data-output="speed">1.00×</output>
        <input data-setting="speed" type="range" min=".25" max="2" step=".05" value="1">
      </label>
      <label>DENSITY <output data-output="density">1.00×</output>
        <input data-setting="density" type="range" min=".4" max="1.6" step=".05" value="1">
      </label>
      <label class="toggle-row">TRAILS
        <input data-setting="trails" type="checkbox" checked>
      </label>
      <label>SEED
        <input data-setting="seed" type="number" value="${settings.seed}" min="1" max="999999">
      </label>
      <button class="debug-apply" type="button" data-apply-debug>APPLY / RESTART</button>
      <p>Eco 模式会让 Sparks 强制走 Canvas 2D fallback。其他参数重启当前场景后生效。</p>
    </aside>`;
}

function renderGallery() {
  activeCleanup?.();
  activeCleanup = null;
  activeWork = null;
  document.body.classList.remove("scene-open");
  app.innerHTML = galleryMarkup() + debugMarkup();
  bindGlobalUi();
  runPreviews();
  history.replaceState(null, "", location.pathname);
}

function renderScene(work) {
  activeCleanup?.();
  activeCleanup = null;
  activeWork = work;
  document.body.classList.add("scene-open");
  app.innerHTML = `
    <main class="scene-shell">
      <div class="scene-topbar">
        <button type="button" data-back>← INDEX</button>
        <div>
          <strong>${esc(work.title)}</strong>
          <span>${esc(work.subtitle)}</span>
        </div>
        <div class="scene-tech">${esc(work.renderer)}</div>
        <button type="button" data-open-debug>GUI / G</button>
      </div>
      <section class="scene-root scene-root--${esc(work.tone)}" data-root="${esc(work.id)}">
        <div class="scene-caption">
          <span>MOVE / DRAG / TYPE</span>
          <span>${reducedMotion ? "REDUCED MOTION" : "REALTIME"}</span>
        </div>
      </section>
      <div class="scene-help">${esc(work.desc)}</div>
    </main>
    ${debugMarkup()}`;
  bindGlobalUi();
  app.querySelector("[data-back]").addEventListener("click", renderGallery);
  const root = app.querySelector("[data-root]");
  activeCleanup = mounts[work.id](root, reducedMotion ? { ...settings, speed: 0.25, density: 0.5 } : settings);
  history.replaceState(null, "", `#${work.id}`);
}

function bindGlobalUi() {
  const panel = app.querySelector(".debug-panel");
  const setPanel = (open) => {
    panel.classList.toggle("is-open", open);
    panel.setAttribute("aria-hidden", String(!open));
  };
  app.querySelectorAll("[data-open-debug]").forEach((button) => button.addEventListener("click", () => setPanel(true)));
  app.querySelector("[data-close-debug]").addEventListener("click", () => setPanel(false));
  app.querySelector("[data-apply-debug]").addEventListener("click", () => {
    panel.querySelectorAll("[data-setting]").forEach((input) => {
      const key = input.dataset.setting;
      settings[key] = input.type === "checkbox" ? input.checked : input.type === "number" || input.type === "range" ? Number(input.value) : input.value;
    });
    if (activeWork) renderScene(activeWork);
    else renderGallery();
  });
  panel.querySelectorAll('input[type="range"]').forEach((input) => {
    input.addEventListener("input", () => {
      panel.querySelector(`[data-output="${input.dataset.setting}"]`).value = `${Number(input.value).toFixed(2)}×`;
    });
  });
  window.onkeydown = (event) => {
    if (event.key.toLowerCase() === "g" && !["INPUT", "SELECT"].includes(document.activeElement?.tagName)) {
      setPanel(!panel.classList.contains("is-open"));
    }
    if (event.key === "Escape" && activeWork) renderGallery();
  };
}

function paintPreview(canvas, id, time) {
  const rect = canvas.getBoundingClientRect();
  const dpr = Math.min(devicePixelRatio || 1, 1.25);
  const width = Math.max(1, Math.round(rect.width * dpr));
  const height = Math.max(1, Math.round(rect.height * dpr));
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  const ctx = canvas.getContext("2d");
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const w = rect.width;
  const h = rect.height;
  const cx = w / 2;
  const cy = h / 2;
  const ink = "#17140f";
  const red = "#b3372b";
  const paper = "#e9e1ce";
  ctx.clearRect(0, 0, w, h);
  if (id === "sparks" || id === "rain") {
    ctx.fillStyle = id === "sparks" ? "#080d15" : "#111827";
    ctx.fillRect(0, 0, w, h);
  } else {
    ctx.fillStyle = id === "curtain" ? "#dfc49b" : paper;
    ctx.fillRect(0, 0, w, h);
  }
  if (id === "heart") {
    ctx.fillStyle = ink;
    ctx.beginPath();
    ctx.moveTo(cx, cy + 45);
    ctx.bezierCurveTo(cx - 85, cy - 10, cx - 50, cy - 75, cx, cy - 32);
    ctx.bezierCurveTo(cx + 50, cy - 75, cx + 85, cy - 10, cx, cy + 45);
    ctx.fill();
    for (let i = 0; i < 14; i += 1) {
      const a = time * 0.00035 + i * 2.2;
      ctx.fillStyle = i % 4 ? "#d3a91e" : red;
      ctx.save();
      ctx.translate(cx + Math.cos(a) * (60 + i * 5), cy + Math.sin(a * 1.2) * (40 + i * 3));
      ctx.rotate(a);
      ctx.fillRect(-5, -2, 10, 4);
      ctx.restore();
    }
  } else if (id === "yanfan" || id === "typing" || id === "control") {
    ctx.strokeStyle = "rgba(23,20,15,.4)";
    for (let i = 0; i < 7; i += 1) {
      ctx.beginPath();
      ctx.moveTo(w * 0.15, h * 0.25 + i * 25);
      ctx.lineTo(w * 0.85, h * 0.25 + i * 25);
      ctx.stroke();
    }
    for (let i = 0; i < 16; i += 1) {
      const a = time * 0.0005 + i;
      const x = cx + Math.cos(a) * (40 + (i % 5) * 14);
      const y = cy + Math.sin(a * 1.3) * 90;
      ctx.fillStyle = i % 7 ? ink : red;
      ctx.beginPath();
      ctx.moveTo(x - 6, y);
      ctx.quadraticCurveTo(x, y - 8, x + 7, y);
      ctx.quadraticCurveTo(x, y - 2, x - 6, y);
      ctx.fill();
    }
  } else if (id === "puppet") {
    const sway = Math.sin(time * 0.001) * 24;
    ctx.strokeStyle = ink;
    ctx.lineWidth = 5;
    [[cx + sway, 85, cx, 160], [cx, 160, cx, 250], [cx, 185, cx - 55, 255], [cx, 185, cx + 55, 255], [cx, 250, cx - 42, 360], [cx, 250, cx + 42, 360]].forEach(([x1, y1, x2, y2]) => {
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    });
    ctx.fillStyle = red;
    ctx.beginPath();
    ctx.arc(cx, 130, 18, 0, Math.PI * 2);
    ctx.fill();
  } else if (id === "sparks") {
    for (let i = 0; i < 160; i += 1) {
      const a = i * 2.399 + time * 0.00018;
      const r = ((i * 19) % 120) + 10;
      ctx.fillStyle = i % 17 ? "rgba(232,217,184,.75)" : red;
      ctx.fillRect(cx + Math.cos(a) * r, cy + Math.sin(a * 1.17) * r * 0.7, 2, 2);
    }
  } else if (id === "rain") {
    for (let i = 0; i < 40; i += 1) {
      ctx.strokeStyle = `rgba(210,225,240,${0.12 + (i % 5) * 0.05})`;
      const x = (i * 47 + time * 0.03) % w;
      const y = (i * 91 + time * 0.06) % h;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x - 4, y + 22);
      ctx.stroke();
    }
    ctx.fillStyle = "#d89245";
    for (let i = 0; i < 14; i += 1) ctx.fillRect((i * 73) % w, h * 0.72 + (i % 4) * 17, 7, 10);
  } else if (id === "curtain") {
    for (let col = 0; col < 19; col += 1) {
      for (let row = 0; row < 28; row += 1) {
        const x = (col / 18) * w + Math.sin(time * 0.001 + row * 0.2 + col) * (row * 0.18);
        const y = (row / 27) * h;
        ctx.fillStyle = row % 7 ? "rgba(74,44,27,.34)" : "rgba(176,52,39,.48)";
        ctx.beginPath();
        ctx.arc(x, y, 2.1, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
}

function runPreviews() {
  const canvases = [...app.querySelectorAll("[data-preview]")];
  let running = true;
  const frame = (time) => {
    if (!running) return;
    canvases.forEach((canvas) => paintPreview(canvas, canvas.dataset.preview, time));
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
  app.querySelectorAll("[data-scene]").forEach((button) => {
    button.addEventListener("click", () => {
      running = false;
      renderScene(WORKS.find((work) => work.id === button.dataset.scene));
    });
  });
  activeCleanup = () => {
    running = false;
  };
}

const route = location.hash.slice(1);
const initialWork = WORKS.find((work) => work.id === route);
if (initialWork) renderScene(initialWork);
else renderGallery();
